import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, LoadingPlaceholder } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shown in place of the payment method while the backend fulfills a confirmed sweetpass payment.
 * When it takes longer than expected, the customer can cancel the payment.
 */
export const SweetpassCheckoutConfirmingPayment = (props: Props) => {
  const { isDelayed, isCanceling, onCancelPress } = props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles.container} accessibilityLiveRegion="polite">
      <BodyText sizeMatch={['24']} style={styles.title}>
        <FormattedMessage {...messages.title} />
      </BodyText>

      <LoadingPlaceholder rows={3} rowHeight={48} palette="cream" />

      {isDelayed ? (
        <>
          <BodyText sizeMatch={['16']} style={styles.body}>
            <FormattedMessage {...messages.body} />
          </BodyText>

          <Button
            size="large"
            palette="kaleLightest"
            style={styles.cta}
            aria-label={formatMessage(messages.cta)}
            isLoading={isCanceling}
            onPress={onCancelPress}
          >
            <FormattedMessage {...messages.cta} />
          </Button>
        </>
      ) : null}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['4'],
  },
  title: {
    textAlign: 'center',
    color: theme.colors.KALE,
  },
  body: {
    textAlign: 'center',
  },
  cta: {
    minHeight: 64,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Waiting for payment confirmation...',
    description: 'Sweetpass | Checkout | Confirming payment | Title',
  },
  body: {
    defaultMessage: `This is taking longer than expected.
You might have to confirm the payment again.`,
    description: 'Sweetpass | Checkout | Confirming payment | Delayed body',
  },
  cta: {
    defaultMessage: 'Cancel payment',
    description: 'Sweetpass | Checkout | Confirming payment | Cancel CTA',
  },
});

// ─── Props ───────────────────────────────────────────────────────────────────

type Props = {
  isDelayed: boolean;
  isCanceling: boolean;
  onCancelPress: () => void;
};
